import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { visitsAPI, assessmentsAPI, imagesAPI, reportsAPI } from '../services/api';
import './VisitDetails.css';

function VisitDetails() {
  const { visitId } = useParams();
  const navigate = useNavigate();

  const [visit, setVisit] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [nonCompliant, setNonCompliant] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [visitId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [visitRes, assessRes, nonCompRes, imagesRes] = await Promise.all([
        visitsAPI.getById(visitId),
        assessmentsAPI.getByVisit(visitId),
        assessmentsAPI.getMandatoryNonCompliant(visitId),
        imagesAPI.getByVisit(visitId),
      ]);

      setVisit(visitRes.data.data);
      setAssessments(assessRes.data.data);
      setNonCompliant(nonCompRes.data.data);
      setImages(imagesRes.data.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Failed to load visit details');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      // Call the backend API to generate report
      const response = await reportsAPI.generate(visitId);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GRIHA_Report_Visit_${visitId}_${new Date().getTime()}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('Report generated and downloaded successfully!');
    } catch (err) {
      console.error('Error generating report:', err);
      const errorMessage = err.response?.data?.message || 'Failed to generate report. Please ensure all assessments are saved.';
      alert(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="loading">Loading visit details...</div>;
  if (!visit) return <div className="error">Visit not found</div>;

  const assessedCount = assessments.filter((a) => a.compliance_status).length;
  const totalCount = assessments.length;
  const completionPercentage = totalCount > 0 ? Math.round((assessedCount / totalCount) * 100) : 0;

  return (
    <div className="visit-details-container">
      <div className="visit-header">
        <div>
          <h2>{visit.project_name}</h2>
          <p className="visit-meta">
            <strong>Project Code:</strong> {visit.project_code} | <strong>Location:</strong>{' '}
            {visit.location}
          </p>
          <p className="visit-meta">
            <strong>Visit Date:</strong> {new Date(visit.visit_date).toLocaleDateString()} |{' '}
            <strong>Assessors:</strong> {visit.assessors?.map((a) => a.full_name).join(', ')}
          </p>
        </div>
        <div className="header-actions">
          <Link to={`/visits/${visitId}/assess`} className="btn btn-primary">
            Continue Assessment
          </Link>
          <button
            onClick={handleGenerateReport}
            disabled={generating}
            className="btn btn-success"
          >
            {generating ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      <div className="visit-stats">
        <div className="stat-card">
          <div className="stat-value">{completionPercentage}%</div>
          <div className="stat-label">Completion</div>
          <div className="stat-detail">
            {assessedCount} of {totalCount} criteria assessed
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{images.length}</div>
          <div className="stat-label">Images</div>
          <div className="stat-detail">Reference photos uploaded</div>
        </div>

        <div className="stat-card">
          <div className={`stat-value ${nonCompliant.length > 0 ? 'warning' : 'success'}`}>
            {nonCompliant.length}
          </div>
          <div className="stat-label">Non-Compliant</div>
          <div className="stat-detail">Mandatory criteria</div>
        </div>
      </div>

      {nonCompliant.length > 0 && (
        <div className="non-compliant-section">
          <h3>⚠️ Mandatory Non-Compliant Criteria</h3>
          <ul>
            {nonCompliant.map((item, index) => (
              <li key={index}>
                <strong>{item.criterion_code}:</strong> {item.criterion_name}
              </li>
            ))}
          </ul>
          <p className="warning-text">
            Non-compliance with mandatory criteria may render the project ineligible for GRIHA
            Rating.
          </p>
        </div>
      )}

      <div className="assessments-summary">
        <h3>Assessment Summary by Category</h3>
        {(() => {
          const categories = {};
          assessments.forEach((a) => {
            if (!categories[a.category]) {
              categories[a.category] = { total: 0, assessed: 0, compliant: 0 };
            }
            categories[a.category].total++;
            if (a.compliance_status) {
              categories[a.category].assessed++;
              if (a.compliance_status === 'YES') {
                categories[a.category].compliant++;
              }
            }
          });

          return Object.entries(categories).map(([category, stats]) => (
            <div key={category} className="category-summary">
              <h4>{category}</h4>
              <div className="category-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(stats.assessed / stats.total) * 100}%` }}
                  />
                </div>
                <span className="progress-text">
                  {stats.assessed} / {stats.total} assessed
                  {stats.assessed > 0 && ` (${stats.compliant} compliant)`}
                </span>
              </div>
            </div>
          ));
        })()}
      </div>

      {images.length > 0 && (
        <div className="images-gallery">
          <h3>Reference Images ({images.length})</h3>
          <div className="images-grid">
            {images.slice(0, 12).map((img) => (
              <div key={img.image_id} className="gallery-image">
                <img src={imagesAPI.getImageUrl(img.image_id)} alt={`Image ${img.image_number}`} />
                <span>Image {img.image_number}</span>
              </div>
            ))}
          </div>
          {images.length > 12 && (
            <p className="more-images">+ {images.length - 12} more images</p>
          )}
        </div>
      )}

      <div className="construction-status">
        <h3>Construction Status</h3>
        <p>{visit.construction_status || 'No status information provided'}</p>
        {visit.project_stage && (
          <p>
            <strong>Stage:</strong> {visit.project_stage}
          </p>
        )}
      </div>
    </div>
  );
}

export default VisitDetails;